/*
 * TODO: All notifications should come from comms-center, but it doesn't yet
 * handle all notifications. Until that work is done, see TempNotifications.tsx
 * for what is currently in use.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Alert,
  AlertColor,
  Box,
  Card,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridCellParams } from '@mui/x-data-grid';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CancelIcon from '@mui/icons-material/Close';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Notification } from '../../../data/models/notification.model';
import * as notificationSlice from '../../../data/notification.slice';
import TripPackageDialog from './TripPackageDialog';
import { DateTime } from 'luxon';

const PREFIX = 'Notifications';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
}));

export default function Notifications() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    AlertColor | undefined
  >();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [tripPackageDialogOpen, setTripPackageDialogOpen] =
    React.useState<boolean>(false);
  const [providerId, setProviderId] = React.useState<number>(0);
  const [packageUUID, setPackageUUID] = React.useState<string>('');

  const loading = useAppSelector(notificationSlice.selectLoadingNotifications);
  const notifications = useAppSelector(notificationSlice.selectNotifications);

  React.useEffect(() => {
    dispatch(notificationSlice.fetchNotificationsAsync());
  }, []);

  const renderDateTime = (params: GridCellParams) => {
    const d = DateTime.fromISO(params.row.createdOnUTC);
    return d.toFormat('M/d/yyyy h:mm a');
  };

  const renderMessage = (params: GridCellParams) => {
    return params.row.notificationMessage;
  };

  const onDismissNotification = (notificationID: number) => {
    dispatch(notificationSlice.dismissNotificationAsync(notificationID));
  };

  const handleTripPackageClicked = (notification: Notification) => {
    setProviderId(notification.recipients[0].recipient);
    setPackageUUID('' + notification.customMetadata.packageUUID);
    setTripPackageDialogOpen(true);
  };

  const getActionComponent = (notification: Notification): React.ReactNode => {
    if (notification.topic === 'tripLegPackageOffered') {
      return (
        <>
          <Tooltip title={t('notification.act')}>
            <>
              <IconButton
                data-testid="edit"
                onClick={() => {
                  handleTripPackageClicked(notification);
                }}
                size="medium"
                color="primary">
                <SettingsOutlinedIcon />
              </IconButton>

              <IconButton
                data-testid="dismiss"
                disabled={loading}
                onClick={() =>
                  onDismissNotification(notification.notificationID)
                }
                size="medium"
                color="primary">
                <CancelIcon />
              </IconButton>
            </>
          </Tooltip>
        </>
      );
    }

    return (
      <>
        <IconButton
          data-testid="dismiss"
          disabled={loading}
          onClick={() => onDismissNotification(notification.notificationID)}
          size="medium"
          color="primary">
          <CancelIcon />
        </IconButton>
      </>
    );
  };

  const columns = [
    {
      field: 'time',
      headerName: t('notification.dateTime'),
      width: 150,
      sortable: false,
      renderCell: renderDateTime,
    },
    {
      field: 'message',
      headerName: t('notification.description'),
      flex: 1,
      sortable: false,
      renderCell: renderMessage,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 100,
      sortable: false,
      renderCell: (params: GridCellParams) => getActionComponent(params.row),
    },
  ];

  const closeTripPackageDialog = (msg?: string, isError?: boolean) => {
    if (msg) {
      setSnackbarMessage(msg);
      setSnackbarSeverity(isError ? 'error' : 'success');
      setSnackbarOpen(true);
    }
    setTripPackageDialogOpen(false);
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h4">{t('common.notifications')}</Typography>
      </Box>
      <Card>
        <DataGrid
          getRowId={(row) => row.notificationID}
          rows={[...notifications].reverse()} // Sort by DESC
          onRowClick={(params) => {
            handleTripPackageClicked(params.row as Notification);
          }}
          columns={columns}
          autoHeight={true}
          loading={loading}
          hideFooter={true}
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
        />
      </Card>
      <TripPackageDialog
        open={tripPackageDialogOpen}
        packageUUID={packageUUID}
        close={closeTripPackageDialog}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => closeSnackbar()}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}
